import { useMemo, useRef, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import {
  Body,
  Caption,
  VBadge,
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  VRadioGroup,
  VTextarea,
} from "@/components/ui";
import { useSaveTemplate } from "@/domains/communication/api/mutations";
import type {
  EmailTemplate,
  EmailTemplateAudience,
} from "@/domains/communication/api/types";

const AUDIENCE_OPTIONS: ReadonlyArray<{ value: EmailTemplateAudience; label: string }> = [
  { value: "requester", label: "Aanvrager" },
  { value: "partner", label: "Plaatsingsfirma" },
  { value: "internal", label: "Intern" },
];

const SUGGESTED_VARIABLES: ReadonlyArray<{ name: string; description: string }> = [
  { name: "requesterFirstName", description: "Voornaam aanvrager" },
  { name: "requestNumber", description: "Aanvraagnummer" },
  { name: "requestTitle", description: "Titel aanvraag" },
  { name: "caseNumber", description: "Dossiernummer" },
  { name: "caseTitle", description: "Titel dossier" },
  { name: "address", description: "Locatie" },
  { name: "district", description: "Stadsdeel" },
  { name: "startDate", description: "Startdatum" },
  { name: "endDate", description: "Einddatum" },
  { name: "deadline", description: "Deadline" },
  { name: "assetSummary", description: "Asset overzicht" },
];

const VAR_RE = /\{\{(\w+)\}\}/g;

function extractVariables(...texts: ReadonlyArray<string>): ReadonlyArray<string> {
  const seen = new Set<string>();
  for (const text of texts) {
    for (const match of text.matchAll(VAR_RE)) {
      seen.add(match[1]);
    }
  }
  return [...seen];
}

export type TemplateFormMode =
  | { kind: "create" }
  | { kind: "edit"; template: EmailTemplate }
  | { kind: "duplicate"; source: EmailTemplate };

type TemplateFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: TemplateFormMode;
};

type FormState = {
  name: string;
  audience: EmailTemplateAudience;
  subject: string;
  body: string;
};

function initialState(mode: TemplateFormMode): FormState {
  if (mode.kind === "edit") {
    return {
      name: mode.template.name,
      audience: mode.template.audience,
      subject: mode.template.subject,
      body: mode.template.body,
    };
  }
  if (mode.kind === "duplicate") {
    return {
      name: `${mode.source.name} (kopie)`,
      audience: mode.source.audience,
      subject: mode.source.subject,
      body: mode.source.body,
    };
  }
  return {
    name: "",
    audience: "requester",
    subject: "",
    body: "",
  };
}

function makeId(): string {
  return `tpl-${Date.now().toString(36)}`;
}

export function TemplateFormDialog({
  open,
  onOpenChange,
  mode,
}: TemplateFormDialogProps): React.JSX.Element {
  const [form, setForm] = useState<FormState>(() => initialState(mode));
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const save = useSaveTemplate();

  const detected = useMemo(
    () => extractVariables(form.subject, form.body),
    [form.subject, form.body],
  );

  const titleText =
    mode.kind === "edit"
      ? "Template bewerken"
      : mode.kind === "duplicate"
        ? "Template dupliceren"
        : "Nieuwe template";

  const submitLabel = mode.kind === "edit" ? "Opslaan" : "Aanmaken";

  function insertVariable(varName: string): void {
    const token = `{{${varName}}}`;
    const el = bodyRef.current;
    if (!el) {
      setForm((s) => ({ ...s, body: `${s.body}${token}` }));
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const nextBody = form.body.slice(0, start) + token + form.body.slice(end);
    setForm((s) => ({ ...s, body: nextBody }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) return;
    const id = mode.kind === "edit" ? mode.template.id : makeId();
    const template: EmailTemplate = {
      id,
      name: form.name.trim(),
      audience: form.audience,
      subject: form.subject.trim(),
      body: form.body,
      variables: detected,
    };
    save.mutate(template, {
      onSuccess: () => onOpenChange(false),
    });
  }

  return (
    <VDialog open={open} onOpenChange={onOpenChange}>
      <VDialogContent size="xl" title={titleText}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-[1fr,16rem]">
            <div className="space-y-4">
              <VField label="Naam" required htmlFor="tpl-name">
                <VInput
                  id="tpl-name"
                  value={form.name}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Bv. Plaatsing gepland"
                />
              </VField>

              <VField label="Onderwerp" required htmlFor="tpl-subject" hint="Mag variabelen bevatten.">
                <VInput
                  id="tpl-subject"
                  value={form.subject}
                  onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                  placeholder="Bv. Plaatsingsopdracht — dossier #{{caseNumber}}"
                />
              </VField>

              <VField label="Bericht" required htmlFor="tpl-body">
                <VTextarea
                  id="tpl-body"
                  ref={bodyRef}
                  value={form.body}
                  onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
                  rows={12}
                  placeholder="Beste {{requesterFirstName}}, ..."
                  className="font-mono text-sm"
                />
              </VField>

              {detected.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Caption tone="muted" className="mr-1">
                    Gedetecteerde variabelen:
                  </Caption>
                  {detected.map((v) => (
                    <VBadge key={v} color="info">
                      {`{{${v}}}`}
                    </VBadge>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="space-y-4 rounded-md border border-border bg-muted/30 p-3">
              <VField label="Doelgroep" required>
                <VRadioGroup
                  name="audience"
                  value={form.audience}
                  options={AUDIENCE_OPTIONS}
                  onChange={(v) => setForm((s) => ({ ...s, audience: v }))}
                />
              </VField>

              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="size-3.5 text-primary-700" aria-hidden />
                  <Caption tone="default" className="font-semibold">
                    Variabelen invoegen
                  </Caption>
                </div>
                <Body tone="muted" className="text-xs">
                  Klik om in te voegen op de cursorpositie.
                </Body>
                <div className="flex flex-wrap gap-1">
                  {SUGGESTED_VARIABLES.map((v) => (
                    <button
                      key={v.name}
                      type="button"
                      onClick={() => insertVariable(v.name)}
                      title={v.description}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <Plus className="size-3" aria-hidden />
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <VButton
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={save.isPending}
            >
              Annuleren
            </VButton>
            <VButton type="submit" disabled={save.isPending}>
              {save.isPending ? "Opslaan…" : submitLabel}
            </VButton>
          </div>
        </form>
      </VDialogContent>
    </VDialog>
  );
}
